import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { CITIES, CITY_SLUGS } from "@/lib/citiesData";
import { INDUSTRIES, INDUSTRY_SLUGS } from "@/lib/industriesData";

const Footer = () => {
  const { t, lang } = useApp();
  const year = new Date().getFullYear();
  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/portfolio", label: t.nav.portfolio },
    { to: "/pricing", label: t.nav.pricing },
    { to: "/affordable-website-development", label: lang === "de" ? "Günstige Website-Entwicklung" : "Affordable Website Development" },
    { to: "/contact", label: t.nav.contact },
  ];
  const legal =
    lang === "de"
      ? [
          { to: "/impressum", label: "Impressum" },
          { to: "/datenschutz", label: "Datenschutz" },
        ]
      : [
          { to: "/imprint", label: "Imprint" },
          { to: "/privacy", label: "Privacy" },
        ];
  return (
    <footer
      data-testid="site-footer"
      className="mt-24 border-t border-[#0A0A0A] bg-[#FAFAFA] text-[#0A0A0A]"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <img
              src="/brand/netovibe-logo.png"
              alt="NetoVibe — affordable web design agency"
              className="h-16 lg:h-20 w-auto object-contain"
              width="200"
              height="80"
            />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-[#525252]">
              {t.footer.tagline}
            </p>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs tracking-[0.2em] uppercase font-bold">
              {t.footer.linksTitle}
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              {links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="hover:text-[#3A00FF] transition-colors"
                    data-testid={`footer-link-${l.to.replace(/\//g, "-").replace(/^-/, "") || "home"}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs tracking-[0.2em] uppercase font-bold">
              {lang === "de" ? "Standorte" : "Locations"}
            </div>
            <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {CITY_SLUGS.map((slug) => (
                <li key={slug}>
                  <Link
                    to={`/website-development/${slug}`}
                    className="hover:text-[#3A00FF] transition-colors"
                    data-testid={`footer-city-${slug}`}
                  >
                    {CITIES[slug].name}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-6 text-xs tracking-[0.2em] uppercase font-bold">
              {lang === "de" ? "Branchen" : "Industries"}
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              {INDUSTRY_SLUGS.map((slug) => (
                <li key={slug}>
                  <Link
                    to={`/website-development-for/${slug}`}
                    className="hover:text-[#3A00FF] transition-colors"
                    data-testid={`footer-industry-${slug}`}
                  >
                    {INDUSTRIES[slug][lang]?.name || INDUSTRIES[slug].en.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs tracking-[0.2em] uppercase font-bold">
              {t.footer.contactTitle}
            </div>
            <ul className="mt-4 space-y-2 text-sm text-[#525252]">
              <li>
                <a
                  href="mailto:netovibe@outlook.com"
                  className="hover:text-[#3A00FF] transition-colors"
                  data-testid="footer-email"
                >
                  netovibe@outlook.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+4915569445772"
                  className="hover:text-[#3A00FF] transition-colors"
                  data-testid="footer-phone"
                >
                  +49 155 6944 5772
                </a>
              </li>
              <li>EN · DE</li>
            </ul>
          </div>

          <div className="md:col-span-1">
            <div className="text-xs tracking-[0.2em] uppercase font-bold">
              {t.footer.legalTitle}
            </div>
            <ul className="mt-4 space-y-2 text-sm text-[#525252]">
              {legal.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="hover:text-[#3A00FF] transition-colors"
                    data-testid={`footer-legal-${l.to.replace("/", "")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-[#0A0A0A]/15">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[#525252]">
          <div>© {year} NetoVibe. {t.footer.rights}</div>
          <div className="tracking-[0.2em] uppercase">Berlin · Remote worldwide</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
